<template>
  <div
    id="reports"
    class="mt-3">
    <!-- Event Search Modal -->
    <b-modal
      id="event-search-modal"
      ref="eventSearchModal"
      size="lg"
      v-model="eventSearchModal"
      title="Search Events"
      @hidden="events = []; eventConstraint = '';"
      hide-footer>
      <b-form
        id="event-search-form"
        name="eventSearchForm"
        @submit.prevent="addEventIDs">
        <!-- Events Constraint -->
        <b-form-input
          class="flex-fill mb-3"
          type="text"
          ref="constraintEvents"
          placeholder="Constraint Events"
          v-model="eventConstraint">
        </b-form-input>
        <!-- / Events Constraint -->

        <!-- Events Table -->
        <h6>Event Results:</h6>
        <b-table
          id="event-search-table"
          striped
          hover
          selectable
          stacked="lg"
          small
          show-empty
          select-mode="multiple"
          @row-selected="selectEvent"
          :items="filteredEvents"
          :fields="eventFields"
          empty-text="No Events to show.">
        </b-table>
        <!-- / Events Table -->

        <!-- Form Buttons -->
        <div class="mt-3 d-flex justify-content-end">
          <b-button
            type="submit"
            variant="success">
            Select Events
            <font-awesome-icon icon="check-circle"></font-awesome-icon>
          </b-button>

          <b-button
            type="button"
            class="ml-3"
            variant="danger"
            @click="$refs.eventSearchModal.hide();">
            Cancel
            <font-awesome-icon icon="times-circle"></font-awesome-icon>
          </b-button>
        </div>
        <!-- / Form Buttons -->
      </b-form>
    </b-modal>
    <!-- / Event Search Modal -->

    <!-- Fields & Constraints -->
    <form
      id="fields-and-constraints-form"
      name="fieldsAndConstraintsForm"
      class="container-fluid"
      @submit.prevent="generateReport(false)">
      <!-- Page Header -->
      <div class="row">
        <div class="col">
          <div class="d-flex flex-column flex-sm-row align-items-center">
            <h3 class="mb-0 mr-3 text-center text-sm-left">
              Custom Report Generator
            </h3>
            <small>
              Select desired Fields & Constraints and use
              <font-awesome-icon icon="check"></font-awesome-icon> to see the results or
              <font-awesome-icon icon="download"></font-awesome-icon> for a CSV download.
            </small>

            <!-- Report Type -->
            <div class="ml-auto d-flex align-items-center">
              <div class="mr-3">
                Report Type
                <span
                  v-b-tooltip.hover
                  title="
                    Registrations returns data based on registrants.
                    Transactions returns data based on financial transactions.
                  ">
                  <font-awesome-icon
                    size="sm"
                    icon="info-circle">
                  </font-awesome-icon>
                </span>:
              </div>

              <div>
                <b-form-radio
                  v-model="reportType"
                  name="reportType"
                  value="registrations">
                  Registrations
                </b-form-radio>

                <b-form-radio
                  v-model="reportType"
                  name="reportType"
                  value="payment_transactions">
                  Transactions
                </b-form-radio>
              </div>
            </div>
            <!-- / Report Type -->
          </div>
          <hr class="mb-0">
        </div>
      </div>
      <!-- / Page Header -->

      <!-- Fields -->
      <div id="fields">
        <h6 class="my-3" :class="{'text-danger': selectedFields.length === 0}">
          Fields
          ({{selectedFields.length}})
          <span
            v-b-tooltip.hover.right
            title="Fields are the values you want to see in your report.">
            <font-awesome-icon
              size="sm"
              icon="info-circle">
            </font-awesome-icon>
          </span>
        </h6>

        <!-- Select / Add Field -->
        <div class="row my-3">
          <!-- Field Options -->
          <div class="col-12 mb-3 mb-lg-0 col-lg-5">
            <v-select
              id="report-field-select"
              ref="reportFieldSelect"
              name="reportFieldSelect"
              class="text-capitalize"
              placeholder="Select field"
              label="label"
              :disabled="availableFields.length === 0"
              :options="availableFields"
              @input="addField(selectedField)"
              v-model="selectedField">
            </v-select>
          </div>
          <!-- / Field Options -->

          <!-- Add All Fields -->
          <div class="col-12 col-sm-auto">
            <b-button
              type="button"
              variant="link"
              class="px-0"
              :disabled="availableFields.length === 0"
              @click="addAllFields()">
              Add All Fields ({{fields.length}})
            </b-button>
          </div>
          <!-- / Add All Fields -->

          <!-- Remove All Fields -->
          <div class="col-12 col-sm-auto">
            <b-button
              type="button"
              variant="link"
              class="px-0"
              :disabled="selectedFields.length === 0"
              @click="removeAllFields()">
              Remove All Fields
            </b-button>
          </div>
          <!-- / Remove All Fields -->
        </div>
        <!-- / Select / Add Field -->

        <!-- Selected Fields -->
        <div
          v-for="(field, index) in selectedFields"
          :key="index"
          class="constraint row">
          <div
            class="col-sm d-flex align-items-center justify-content-between">
            <!-- Label -->
            <div>{{index + 1}}. {{field.label}}</div>
            <!-- / Label -->

            <!-- Remove -->
            <b-button
              type="button"
              variant="link"
              @click="removeField(index)">
              Remove Field
            </b-button>
            <!-- / Remove -->
          </div>
        </div>
        <!-- / Selected Fields -->
      </div>
      <!-- / Fields -->

      <hr>

      <!-- Constraints -->
      <div id="constraints">
        <h6 class="my-3" :class="{'text-danger': selectedConstraints.length === 0}">
          Constraints
          ({{selectedConstraints.length}})
          <span
            v-b-tooltip.hover.right
            title="Constraints are the limits on which data is included in your report.">
            <font-awesome-icon
              size="sm"
              icon="info-circle">
            </font-awesome-icon>
          </span>
        </h6>

        <div class="d-flex">
          <!-- Registration Status -->
          <b-form-group
            label="Registration Status:">
            <!-- Active Roster -->
            <b-form-radio
              v-model="registrationStatus"
              name="registrationStatus"
              value="active">
              Active Roster
            </b-form-radio>
            <!-- / Active Roster -->

            <!-- Cancelled -->
            <b-form-radio
              v-model="registrationStatus"
              name="registrationStatus"
              value="canceled">
              Canceled Registrations Only
            </b-form-radio>
            <!-- / Cancelled -->

            <!-- Waitlist -->
            <b-form-radio
              v-model="registrationStatus"
              name="registrationStatus"
              value="waitlist">
              Waitlist Registrations Only
            </b-form-radio>
            <!-- / Waitlist -->

            <!-- Everyone -->
            <b-form-radio
              v-model="registrationStatus"
              name="registrationStatus"
              value="all">
              All Registrations
            </b-form-radio>
            <!-- / Everyone -->
          </b-form-group>
          <!-- / Registration Status -->

          <!-- Registration Status -->
          <b-form-group
            v-if="reportType === 'payment_transactions'"
            class="ml-3"
            label="Transaction Type:">
            <b-form-radio-group
              id="transactonTypes"
              v-model="transactionType"
              :options="transactionTypeOptions"
              name="transactionType"
              stacked>
            </b-form-radio-group>

            <!-- All Types -->
            <b-form-radio
              v-model="transactionType"
              name="transactionType"
              value="all">
              All Transaction Types
            </b-form-radio>
            <!-- / All Types -->
          </b-form-group>
          <!-- / Registration Status -->
        </div>

        <!-- Select / Add Constraint -->
        <div class="row my-3">
          <!-- Constraint Options -->
          <div class="col-12 mb-3 mb-lg-0 col-lg-5">
            <label for="report-constraint-select">Other Constraints:</label>
            <v-select
              id="report-constraint-select"
              ref="reportConstraintSelect"
              name="reportConstraintSelect"
              class="text-capitalize"
              placeholder="Select constraint"
              label="label"
              :disabled="availableConstraints.length === 0"
              :options="availableConstraints"
              @input="addConstraint(selectedConstraint)"
              v-model="selectedConstraint">
            </v-select>
          </div>
          <!-- / Constraint Options -->

          <!-- Remove All Constraints -->
          <div class="col-12 col-sm-auto">
            <label>&nbsp;</label><br>
            <b-button
              type="button"
              variant="link"
              class="px-0"
              :disabled="selectedConstraints.length === 0 && selectedEventsConstraint.length === 0"
              @click="removeAllConstraints()">
              Remove All Constraints
            </b-button>
          </div>
          <!-- / Remove All Constraints -->
        </div>
        <!-- / Select / Add Constraint -->

        <!-- Selected Constraints -->
        <div
          v-for="(constraint, index) in selectedConstraints"
          :key="index"
          class="constraint row pt-2 pb-2">
          <div
            class="col-sm d-flex flex-column flex-sm-row align-items-sm-center">
            <!-- Label -->
            <div>{{index + 1}}. {{constraint.label}}</div>
            <!-- / Label -->

            <!-- Comparator Constraint -->
            <div
              v-if="constraint.comparators.length > 1"
              class="d-flex align-items-center justify-content-center">
              <!-- Select Comparator -->
              <div
                class="ml-sm-2 mr-1 mt-3 mt-sm-0">
                <b-form-select
                  v-model="constraint.comparator">
                  <option selected :value="undefined">Select Comparator</option>
                  <option
                    v-for="c in constraint.comparators"
                    :key="c"
                    :value="c">
                    {{c}}
                  </option>
                </b-form-select>
              </div>
              <!-- / Select Comparator -->

              <!-- Birthdate Select Date -->
              <div
                v-if="constraint.name === 'birthdate'"
                class="ml-1 mt-3 mt-sm-0">
                <b-form-input
                  type="date"
                  v-model="constraint.value">
                </b-form-input>
              </div>
              <!-- / Birthdate Select Date -->

              <!-- Value -->
              <div
                v-else
                class="ml-1 mt-3 mt-sm-0">
                <b-form-input
                  v-model="constraint.value"
                  type="text"
                  placeholder="Enter value">
                </b-form-input>
              </div>
              <!-- / Value -->
            </div>
            <!-- / Comparator Constraint -->

            <!-- Non-Comparator Constraint -->
            <div
              v-else
              class="d-flex align-items-center justify-content-center">
              <div class="ml-1 mt-3 mt-sm-0">
                <!-- Checkbox -->
                <div
                  v-if="constraint.type === 'checkbox'"
                  class="d-flex ml-2">
                  <b-form-radio
                    name="checkbox"
                    :value="true"
                    v-model="constraint.value">
                    True
                  </b-form-radio>
                  <b-form-radio
                    class="ml-3"
                    name="checkbox"
                    :value="false"
                    v-model="constraint.value">
                    False
                  </b-form-radio>
                </div>
                <!-- / Checkbox -->

                <!-- Non-Season Dropdown -->
                <b-form-select
                  v-if="constraint.type === 'dropdown'"
                  v-model="constraint.value"
                  class="ml-2">
                  <option selected :value="undefined">Select Option</option>
                  <option
                    v-for="o in constraint.options"
                    :key="o"
                    :value="o">
                    {{o}}
                  </option>
                </b-form-select>
                <!-- / Non-Season Dropdown -->

                <!-- Season Dropdown -->
                <b-form-select
                  v-if="constraint.type === 'season'"
                  v-model="constraint.value"
                  class="ml-2">
                  <option selected :value="undefined">Select Season</option>
                  <option
                    v-for="season in seasons"
                    :key="season.id"
                    :value="season.id">
                    {{season.name}}
                  </option>
                </b-form-select>
                <!-- / Season Dropdown -->

                <!-- Date & Time Range -->
                <div
                  v-if="constraint.type === 'date-range'"
                  class="d-flex ml-1 align-items-center justify-content-center">
                  <div class="mr-1">between</div>

                  <DateTimePicker
                    class="ml-1"
                    v-model="constraint.start_date_time"
                  />

                  <div class="ml-1">and</div>

                  <DateTimePicker
                    class="ml-1"
                    v-model="constraint.end_date_time"
                  />
                </div>
                <!-- / Date & Time Range -->
              </div>
            </div>
            <!-- / Non-Comparator Constraint -->

            <div class="d-none d-sm-block flex-fill"></div>

            <!-- Remove -->
            <b-button
              class="mt-3 mt-sm-0"
              type="button"
              variant="link"
              @click="removeConstraint(index)">
              Remove Constraint
            </b-button>
            <!-- / Remove -->
          </div>
        </div>
        <!-- / Selected Constraints -->

        <!-- Events -->
        <div
          v-if="selectedEventsConstraint.length"
          class="constraint row pt-2 pb-2">
          <div
            class="col-sm d-flex flex-column flex-sm-row align-items-sm-center">
            <!-- Label -->
            <div>
              {{selectedConstraints.length+1}}. Event{{selectedEventsConstraint.length > 1 ? 's' : ''}}:
              <span
                v-for="(event, index) in selectedEventsConstraint"
                :key="event.id">
                {{event.name}}<span v-if="index+1 !== selectedEventsConstraint.length">, </span>
              </span>
            </div>
            <!-- / Label -->

            <div class="d-none d-sm-block flex-fill"></div>

            <!-- Remove -->
            <b-button
              class="mt-3 mt-sm-0"
              type="button"
              variant="link"
              @click="selectedEventsConstraint = [];">
              Remove Constraint
            </b-button>
            <!-- / Remove -->
          </div>
        </div>
        <!-- / Events -->
      </div>
      <!-- / Constraints -->

      <!-- Report Results -->
      <div
        id="report-results"
        v-if="reportFields.length">
        <hr>
        <h5>
          Results

          <!-- Message Registrants -->
          <b-button
            type="button"
            variant="link"
            :disabled="report.length === 0"
            @click="messageReportRegistrants(report)">
            Message Registrants
          </b-button>
          <!-- / Message Registrants -->

          <!-- Message Registrants -->
          <b-button
            type="button"
            variant="link"
            @click="clearReport()">
            Clear Report
          </b-button>
          <!-- / Message Registrants -->
        </h5>
        <b-table
          striped
          hover
          stacked="lg"
          small
          show-empty
          :items="report"
          :fields="reportFields"
          empty-text="No Results to show.">
          <!-- Completed At -->
          <template
            slot="created_at"
            slot-scope="{ item }">
            {{item.display_created_at}}
          </template>
          <!-- / Completed At -->
        </b-table>
      </div>
      <!-- / Report Results -->

      <div class="corner-buttons">
        <!-- Generate CSV Button -->
        <b-button
          class="shadow ml-3"
          type="button"
          variant="success"
          @click="generateReport(true)"
          :disabled="submitting">
          <font-awesome-icon icon="download"></font-awesome-icon> Download
        </b-button>
        <!-- / Generate CSV Button -->

        <!-- Generate Report Button -->
        <b-button
          class="shadow ml-3"
          type="submit"
          variant="success"
          :disabled="submitting">
          <font-awesome-icon icon="check"></font-awesome-icon> Generate
        </b-button>
        <!-- / Generate CSV Button -->
      </div>
    </form>
    <!-- / Fields & Constraints -->
  </div>
</template>

<script>
import _ from 'lodash';
import vSelect from 'vue-select';
import DataSvc from '../services/data';
import AppMixin from '../services/app-mixin';
import BrowserStorageMixin from '../services/browser-storage-mixin';
import EventBus from '../services/event-bus';
import Formatters from '../services/formatters';

export default {
  name: 'reports',
  components: {
    vSelect,
  },
  mixins: [
    AppMixin,
    BrowserStorageMixin,
  ],
  data() {
    return {
      // fields
      registrationFields: [],
      paymentTransactionFields: [],
      selectedFields: [],
      selectedField: null,
      currencyFields: [
        'amount',
        'amount_fees',
        'amount_kjk_fees',
        'amount_total',
        'total_ordered',
        'total_collected',
        'balance_due',
      ],

      // constraints
      selectedConstraints: [],
      selectedConstraint: null,
      registrationStatus: 'active',

      // events
      events: [],
      eventConstraint: '',
      eventFields: [
        {
          key: 'name',
          label: 'Event',
        },
        {
          key: 'display_start_date_time',
          label: 'Start',
        },
        {
          key: 'display_end_date_time',
          label: 'End',
        },
      ],
      selectedEvents: [],
      selectedEventsConstraint: [],
      eventSearchModal: false,

      // reports
      report: [],
      reportFields: [],
      reportType: 'registrations',
      transactionType: 'all',
      submitting: false,
    };
  },
  watch: {
    reportType() {
      const availableFieldNames = _.map(this.fields, 'name');
      this.selectedFields = _.filter(
        this.selectedFields,
        f => availableFieldNames.includes(f.name),
      );

      this.selectedConstraints = _.filter(
        this.selectedConstraints,
        c => availableFieldNames.includes(c.name),
      );
    },
  },
  computed: {
    // fields
    fields() {
      return this.reportType === 'registrations'
        ? this.registrationFields
        : this.paymentTransactionFields;
    },
    availableFields() {
      return _(this.fields)
        .differenceWith(
          this.selectedFields,
          (field, selected) => field.name === selected.name,
        )
        .orderBy('label')
        .value();
    },

    // constraints
    constraints() {
      const otherConstraints = [
        {
          label: 'Date & Time Range',
          name: 'date-range-report-hardcoded',
          type: 'date-range',
          overall: true,
          comparators: [],
        },
        {
          label: 'Season',
          type: 'season',
          name: 'season-report-hardcoded',
          overall: true,
          comparators: [],
        },
        {
          label: 'Events',
          type: 'events',
          name: 'events',
          events: true,
        },
      ];

      return _(this.fields)
        .reject(f => f.searchable === false)
        .concat(otherConstraints)
        .value();
    },
    availableConstraints() {
      return _(this.constraints)
        .differenceWith(
          this.selectedConstraints,
          (constraint, selected) => constraint.name === selected.name,
        )
        .orderBy('label')
        .value();
    },

    // events
    filteredEvents() {
      return _.filter(
        this.events,
        o => o.name.toLowerCase().includes(this.eventConstraint.toLowerCase()),
      );
    },

    // payment types
    transactionTypeOptions() {
      return _.map(this.transactionTypes, pt => ({
        text: _.startCase(pt),
        value: pt,
      }));
    },
  },
  methods: {
    // fields
    addField(field) {
      this.selectedFields.push(_.cloneDeep(field));
      this.$nextTick(() => {
        this.selectedField = null;
      });
    },
    addAllFields() {
      this.selectedFields = _.cloneDeep(this.fields);
      this.selectedField = null;
    },
    removeAllFields() {
      this.selectedFields = [];
    },
    removeField(index) {
      this.selectedFields.splice(index, 1);
    },

    // constraints
    addConstraint(constraint) {
      if (constraint.events) {
        this.showEventSearch();
      } else {
        const f = _.cloneDeep(constraint);

        if (f.comparators.length === 0) {
          f.comparator = '=';
        } else if (f.comparators.length === 1) {
          [f.comparator] = f.comparators;
        }

        this.selectedConstraints.push(f);
      }

      this.$nextTick(() => {
        this.selectedConstraint = null;
      });
    },
    removeAllConstraints() {
      this.selectedConstraints = [];
      this.selectedEventsConstraint = [];
    },
    removeConstraint(index) {
      this.selectedConstraints.splice(index, 1);
    },

    // events
    showEventSearch() {
      this.$refs.eventSearchModal.show();
      return DataSvc.getEvents(
        this.loggedInUser.actingOrg.id,
        this.loggedInUser.actingSeason.id,
      ).then((events) => {
        this.events = events;
      });
    },
    addEventIDs() {
      // if existing event ID constraint already exists, replace it
      this.selectedEventsConstraint = _.cloneDeep(this.selectedEvents);

      // close modal
      this.$refs.eventSearchModal.hide();
      this.selectedEvents = [];
    },
    selectEvent(selected) {
      this.selectedEvents = _.cloneDeep(selected);
    },

    // reports
    generateReport(downloadCSV) {
      const data = { report_type: this.reportType };

      const fields = _(this.selectedFields)
        .map(field => field.name)
        .value();

      const constraints = _(this.selectedConstraints)
        .reject(constraint => constraint.overall)
        .map(constraint => ({
          field_name: constraint.name,
          comparator: constraint.comparator,
          value: constraint.value,
        }))
        .value();

      const overall = _.filter(this.selectedConstraints, constraint => constraint.overall);

      // add date range
      const dateRange = _.find(overall, constraint => constraint.type === 'date-range');
      if (dateRange) {
        data.start_date_time = dateRange.start_date_time;
        data.end_date_time = dateRange.end_date_time;
      }

      // add season
      const seasons = _(overall)
        .filter(constraint => constraint.label === 'Season')
        .map('value')
        .value();

      if (seasons.length) {
        data.season_ids = seasons;
      }

      // add event ids
      if (this.selectedEventsConstraint.length) {
        data.event_ids = _.map(this.selectedEventsConstraint, 'id');
      }

      // add payment type if using transaction report type
      if (this.reportType === 'payment_transactions' && this.transactionType !== 'all') {
        data.payment_type = this.transactionType;
      }

      // registration status
      switch (this.registrationStatus) {
        // show only active roster
        case 'active':
          data.cancelled = false;
          data.waitlist = false;
          break;

        // show only canceled regisrations
        case 'canceled':
          data.cancelled = true;
          data.waitlist = false;
          break;

        // show only waitlist registrations
        case 'waitlist':
          data.cancelled = false;
          data.waitlist = true;
          break;

        // show all registrations
        default:
          data.cancelled = null;
          data.waitlist = null;
      }

      const generate = (report) => {
        // if no fields selected, display all
        const fieldsToDisplay = this.selectedFields.length
          ? this.selectedFields
          : this.availableFields;

        // create b-table columns based on selected fields
        this.reportFields = _.map(fieldsToDisplay, (field) => {
          const isCurrencyField = this.currencyFields.includes(field.name);
          return {
            key: field.name,
            label: field.label,
            sortable: true,
            class: isCurrencyField ? 'text-right' : '',
            formatter: (value) => {
              let str = value;

              if (value === null) {
                str = 'N/A';
              } else if (isCurrencyField) {
                str = Formatters.currency(value);
              }

              return str;
            },
          };
        });

        // always display timestamp
        this.reportFields.unshift({
          key: this.reportType === 'registrations'
            ? 'created_at'
            : 'transaction_created_at',
          label: 'Completed At',
          sortable: true,
        });

        // set report data
        this.report = report;
      };

      const download = (report) => {
        const url = window.URL.createObjectURL(new Blob([report]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'report.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
      };

      const success = downloadCSV ? download : generate;

      this.showGlobalLoader('Generating report...');
      this.submitting = true;

      return DataSvc.generateReport(
        this.loggedInUser.actingOrg.id,
        fields,
        constraints,
        data,
        downloadCSV,
      ).then(success)
        .finally(() => {
          this.hideGlobalLoader();
          this.submitting = false;
        });
    },
    messageReportRegistrants(report) {
      const emails = _(report)
        .map('email')
        .uniq()
        .value();

      this.messageEmails(emails);
    },
    updateSeasonConstraint(seasonID) {
      this.selectedConstraints = _.reject(this.selectedConstraints, { type: 'season' });

      if (seasonID) {
        this.addConstraint({
          label: 'Season',
          type: 'season',
          overall: true,
          value: seasonID,
          comparators: [],
        });
      }
    },
    clearOrgFieldsAndConstraints() {
      this.selectedEventsConstraint = [];
      this.selectedEvents = [];
      this.selectedFields = _.reject(this.selectedFields, 'custom');
      this.selectedConstraints = _.reject(this.selectedConstraints, 'custom');
    },
    clearReport() {
      this.report = [];
      this.reportFields = [];
    },
  },
  created() {
    this.showGlobalLoader('Loading data...');

    const initReports = () => {
      if (this.loggedInUser.actingSeason.id) {
        this.addConstraint({
          label: 'Season',
          type: 'season',
          overall: true,
          value: this.loggedInUser.actingSeason.id,
          comparators: [],
        });
      }

      // update events when season changes
      EventBus.$on('acting-season-changed', this.updateSeasonConstraint);
      EventBus.$on('acting-org-changed', this.clearOrgFieldsAndConstraints);

      // get fields for all report types
      const reportFieldsPromise = DataSvc.getFields(this.loggedInUser.actingOrg.id, 'registrations');
      const paymentTransactionFields = DataSvc.getFields(this.loggedInUser.actingOrg.id, 'payment_transactions');
      const transactionTypesPromise = DataSvc.getTransactionTypes();

      return Promise.all([
        reportFieldsPromise,
        paymentTransactionFields,
        transactionTypesPromise,
      ]).then((data) => {
        [this.registrationFields, this.paymentTransactionFields, this.transactionTypes] = data;
      });
    };

    this.initPromise.then(initReports).finally(this.hideGlobalLoader);
  },
  destroyed() {
    EventBus.$off('acting-season-changed', this.updateSeasonConstraint);
    EventBus.$off('acting-org-changed', this.clearOrgFieldsAndConstraints);
  },
};
</script>

<style lang="scss">
#reports {
  .constraint {
    &:nth-child(even) {
      background: rgba(0, 0, 0, 0.05)
    }

    &:hover {
      background: rgba(0, 0, 0, 0.075);
    }
  }

  .add-item-dropdown {
    .dropdown-toggle {
      padding-left: 0;
      padding-right: 0;
      margin-top: 0.5em;
      margin-bottom: 0.5em;
    }

    .dropdown-menu {
      max-height: 50vh;
    }
  }

  #generation-disabled {
    pointer-events: none;
  }
}
</style>
